import type { FC, RefObject } from 'react';

import { ClockIcon, PencilIcon } from '@heroicons/react/24/outline';
import cn from 'classnames';

import type { ComposerScheduling } from '../../CommunityPostComposer.type';
import { tr } from '../constants';
import { ToolbarButton } from '../toolbar/ToolbarButton';

const MAX_SHOWN_COUNT = 99;

interface ScheduleClockButtonProps {
  buttonRef?: RefObject<HTMLButtonElement | null>;
  queuedCount?: number;
  onOpen: () => void;
  disabled: boolean;
  translations?: Record<string, string>;
}

export const ScheduleClockButton: FC<ScheduleClockButtonProps> = ({
  buttonRef,
  queuedCount = 0,
  onOpen,
  disabled,
  translations,
}) => {
  const shownCount = queuedCount > MAX_SHOWN_COUNT ? `${MAX_SHOWN_COUNT}+` : String(queuedCount);
  const label = tr(translations, 'community.composer.schedule.open');

  return (
    <ToolbarButton
      buttonRef={buttonRef}
      label={label}
      onClick={onOpen}
      disabled={disabled}
      buttonProps={{
        'aria-label': queuedCount > 0 ? `${label} (${shownCount})` : label,
        // The composer refocuses the clock after the picker, so only a keyboard focus opens the tooltip.
        onFocus: (event) => {
          if (!event.currentTarget.matches(':focus-visible')) event.preventDefault();
        },
      }}
      icon={
        <span className="relative flex">
          <ClockIcon className="size-5" aria-hidden="true" />
          {queuedCount > 0 && (
            <span
              aria-hidden="true"
              className="absolute -right-3 -top-4 flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--community-primary,#000)] px-1 text-[11px] font-semibold leading-none text-[var(--community-text-on-primary,#fff)] ring-2 ring-white"
            >
              {shownCount}
            </span>
          )}
        </span>
      }
    />
  );
};

interface ScheduledTimeChipProps {
  buttonRef?: RefObject<HTMLButtonElement | null>;
  scheduled: NonNullable<ComposerScheduling['scheduled']>;
  onOpen: () => void;
  disabled: boolean;
}

export const ScheduledTimeChip: FC<ScheduledTimeChipProps> = ({ buttonRef, scheduled, onOpen, disabled }) => (
  <div className="flex pt-3">
    <button
      ref={buttonRef}
      type="button"
      disabled={disabled}
      onClick={onOpen}
      aria-label={scheduled.changeLabel}
      className={cn(
        'inline-flex h-8 min-w-0 items-center gap-1.5 rounded-full border border-black/70 bg-white px-3 text-[13px] font-medium leading-4 text-black/70 transition-colors',
        { 'opacity-50 cursor-not-allowed': disabled, 'hover:bg-black/5': !disabled },
      )}
    >
      <ClockIcon className="size-4 shrink-0" aria-hidden="true" />
      <span className="truncate">{scheduled.label}</span>
      <PencilIcon className="size-3.5 shrink-0" aria-hidden="true" />
    </button>
  </div>
);
